import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useBanners } from '../catalog/hooks/use-catalog.js';
import { recordBannerView, recordBannerClick } from '../catalog/api/catalog-api.js';

const SESSION_FLAG_KEY = 'tsg:promo-popup:shown';

/**
 * Dismissible "promotional popup" placement -- fetches the `popup` banner
 * placement and shows the first active banner once per browser session
 * (`sessionStorage` flag, same `tsg:` key-prefix convention as
 * `lib/idempotency.ts`) so it doesn't reappear on every route change or
 * client-side navigation within the same tab. Wired into `AppShell` so it
 * can appear on top of any storefront page.
 *
 * Accessible dialog pattern mirrors `product-listing.tsx`'s filter drawer:
 * `role="dialog"`, `aria-modal`, Escape-to-close, a focus trap, and focus
 * moved into the dialog on open / returned to the trigger element on close.
 *
 * Deliberately does NOT render an `<Seo>` tag or otherwise touch
 * page-level `<title>`/canonical/robots meta -- it is a transient,
 * client-only overlay, not page content, so it must never override
 * whatever indexing directive the actual routed page underneath it set.
 * That is this component's whole answer to "don't let the popup itself
 * become indexable content": it stays outside the SEO surface entirely
 * rather than fighting the page's own `<Seo noindex>`/canonical tags.
 */
export function PromoPopup() {
  const { data } = useBanners('popup');
  const banner = data?.[0];

  const [dismissed, setDismissed] = useState(false);
  const [alreadyShownThisSession, setAlreadyShownThisSession] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const trackedViewId = useRef<string | null>(null);

  useEffect(() => {
    try {
      setAlreadyShownThisSession(sessionStorage.getItem(SESSION_FLAG_KEY) === 'true');
    } catch {
      // sessionStorage unavailable (private browsing, disabled storage) --
      // fail open to "already shown" so the popup simply never renders
      // rather than throwing.
      setAlreadyShownThisSession(true);
    }
  }, []);

  const open = Boolean(banner) && !dismissed && !alreadyShownThisSession;

  useEffect(() => {
    if (!open || !banner) return;
    try {
      sessionStorage.setItem(SESSION_FLAG_KEY, 'true');
    } catch {
      // Best-effort only -- if this throws, the popup may reappear on the
      // next mount within the same session, which is a harmless downgrade.
    }
    if (trackedViewId.current !== banner.id) {
      trackedViewId.current = banner.id;
      recordBannerView(banner.id);
    }
  }, [open, banner]);

  function close() {
    setDismissed(true);
  }

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const container = dialogRef.current;
    const focusables = container?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select');
    focusables?.[0]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab' || !container) return;
      const items = Array.from(container.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select'));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open || !banner) return null;

  const bannerId = banner.id;
  const handleCtaClick = () => recordBannerClick(bannerId);

  const image = banner.mobileMediaUrl ?? banner.mediaUrl;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={close} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-popup-title"
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="focus-visible:ring-brand absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 hover:bg-white focus-visible:outline-none focus-visible:ring-2"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {image && (
          <img src={image} alt="" className="h-48 w-full object-cover" onError={(event) => (event.currentTarget.style.display = 'none')} />
        )}

        <div className="space-y-3 p-6 text-center">
          <p id="promo-popup-title" className="text-xl font-bold text-gray-900">
            {banner.title}
          </p>
          {banner.linkUrl && (
            <a
              href={banner.linkUrl}
              onClick={handleCtaClick}
              className="bg-brand text-brand-foreground hover:bg-brand/90 inline-block rounded-full px-6 py-2.5 text-sm font-semibold"
            >
              {banner.ctaLabel ?? 'Shop now'}
            </a>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
